import { ApiRequestError, createApi } from '@popcart/core/v1';
import { FALLBACK_ERROR, voiceError } from './lib/voice.js';
import { createOpsMapsApi } from './ops/maphealth/lib.js';
import { createOpsAdsApi } from './ops/ads/lib.js';

// Session token: memory + sessionStorage (S-29: never localStorage, so it dies with the tab).
const KEY = 'popcart.backoffice.session';

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

let token: string | null = readStored();
let unauthorized: (() => void) | null = null;

export const sessionToken = {
  get: (): string | null => token,
  set(t: string) {
    token = t;
    try {
      window.sessionStorage.setItem(KEY, t);
    } catch {
      /* private mode: memory only */
    }
  },
  clear() {
    token = null;
    try {
      window.sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  },
};

/** The auth provider registers what to do when the server says 401. */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  unauthorized = fn;
}

const core = createApi({
  baseUrl: '',
  getToken: () => token,
  onUnauthorized: () => unauthorized?.(),
});

// The Ops map endpoints (native maps: landmarks, gaps, traffic coverage) and the Ops ads endpoints
// (docs/HOME-ADS-CONTRACT.md §2) aren't in the core client yet; they sit under it here, and a core
// method of the same name wins once it lands.
export const api = {
  ...createOpsMapsApi({ baseUrl: '', getToken: () => token, onUnauthorized: () => unauthorized?.() }),
  ...createOpsAdsApi({ baseUrl: '', getToken: () => token, onUnauthorized: () => unauthorized?.() }),
  ...core,
};

/** A human message for any error a query or mutation throws, in the §13 voice (lib/voice.js). */
export function errorMessage(err: unknown, fallback = FALLBACK_ERROR): string {
  if (err instanceof ApiRequestError) return voiceError(err, fallback);
  return fallback;
}

export function errorStatus(err: unknown): number | null {
  return err instanceof ApiRequestError ? err.status : null;
}

export { ApiRequestError };
